import produce from 'immer';
import { create } from 'zustand';
import { AccountData } from './useVelocityAccountsStore';

// Shared empty reference so consumers reading the "no accounts" state keep a
// stable array identity across ticks (matters for downstream useMemo deps).
export const EMPTY_ACCOUNTS: AccountData[] = [];

/**
 * Cross-margin liquidation flags for one sub-account, sized for gating a
 * collateral action rather than for alerting. Isolated perp positions are
 * deliberately excluded: they carry their own collateral and don't enter the
 * cross free-collateral figure that `withdraw` / `transfer_deposit` check.
 */
export interface AccountLiquidationFlags {
	/** `UserStatus.BANKRUPT` — the chain rejects withdraw/transfer outright. */
	isBankrupt: boolean;
	/** `UserStatus.BEING_LIQUIDATED` on cross margin. */
	isBeingLiquidated: boolean;
	/** Cross margin below its buffered maintenance requirement. */
	isAtRisk: boolean;
}

/** Stable reference for accounts with no flags set (or not yet subscribed). */
export const NO_LIQUIDATION_FLAGS: AccountLiquidationFlags = {
	isBankrupt: false,
	isBeingLiquidated: false,
	isAtRisk: false,
};

const EMPTY_FLAGS_BY_USER_KEY: Record<string, AccountLiquidationFlags> = {};

const flagsEqual = (
	a: AccountLiquidationFlags | undefined,
	b: AccountLiquidationFlags
) =>
	!!a &&
	a.isBankrupt === b.isBankrupt &&
	a.isBeingLiquidated === b.isBeingLiquidated &&
	a.isAtRisk === b.isAtRisk;

interface AccountsBeingLiquidatedStore {
	set: (fn: (s: AccountsBeingLiquidatedStore) => void) => void;
	get: () => AccountsBeingLiquidatedStore;
	/** Accounts genuinely in `UserStatus.BEING_LIQUIDATED` right now. */
	accountsBeingLiquidated: AccountData[];
	/** Per-sub-account cross-margin flags, keyed by `userKey`. */
	accountLiquidationFlags: Record<string, AccountLiquidationFlags>;
	setAccountsBeingLiquidated: (accounts: AccountData[]) => void;
	setAccountLiquidationFlags: (
		flags: Record<string, AccountLiquidationFlags>
	) => void;
}

/**
 * Holds the set of sub-accounts currently in (or eligible for) liquidation.
 * Populated once by `useSyncAccountsBeingLiquidated` (mounted in `useAppSetup`)
 * and read by consumers via `useAccountsBeingLiquidated` /
 * `useAccountLiquidationFlags`. Replaces the previous react-singleton-hook
 * wrapper so the 1s scan runs exactly once app-wide.
 */
export const useAccountsBeingLiquidatedStore =
	create<AccountsBeingLiquidatedStore>((set, get) => {
		const immerSet = (fn: (s: AccountsBeingLiquidatedStore) => void) =>
			set(produce(fn));

		return {
			set: immerSet,
			get: () => get(),
			accountsBeingLiquidated: EMPTY_ACCOUNTS,
			accountLiquidationFlags: EMPTY_FLAGS_BY_USER_KEY,
			setAccountsBeingLiquidated: (accountsBeingLiquidated) =>
				immerSet((s) => {
					s.accountsBeingLiquidated = accountsBeingLiquidated;
				}),
			setAccountLiquidationFlags: (accountLiquidationFlags) =>
				immerSet((s) => {
					const prev = s.accountLiquidationFlags;
					const nextKeys = Object.keys(accountLiquidationFlags);

					// The 1s scan rebuilds this map every tick. Keep the previous object
					// when nothing changed so per-account selectors don't re-render
					// every second on a new-but-identical entry.
					if (
						nextKeys.length === Object.keys(prev).length &&
						nextKeys.every((userKey) =>
							flagsEqual(prev[userKey], accountLiquidationFlags[userKey])
						)
					) {
						return;
					}

					s.accountLiquidationFlags = nextKeys.length
						? accountLiquidationFlags
						: EMPTY_FLAGS_BY_USER_KEY;
				}),
		};
	});
